import { Select, Spin, Tag } from 'antd';
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import { badValueValidation } from '../../../../modules/crm/inPatient/service/crmUtils';
import { centeredNotification, processSearchInput } from '../../../../utils/utility';
import { KEYS_TO_HANDLE, MAX_CHARACTERS_LIMIT, PAYER_DROPDOWN_CONSTANTS } from '../../../constants/commonConstants';
import { useGetFormConfig } from '../../../hooks/useFormConfig';
import FormItem from '../FormItem/FormItem';
import './formSelect.less';
import { OptGroup } from 'rc-select';
import { isEmpty } from 'lodash';
import { NON_CONFIGURED_PAYERS_LIST_MESSAGE } from '../../../constants/crmConstants';

const { Option } = Select;

const FormSelectPayerDropdown = (props: any): (ReactElement | null) => {
  const [searchValue, setSearchValue] = useState('');
  const [extraRules, setExtraRules] = useState<any[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const selectRef = useRef<any>(null);
  const [fieldInFocus, setFieldInFocus] = useState<boolean>(false);

  const formConfig = useGetFormConfig(props);
  const {
    placeholder, disabled, className, showSearch, mode, onChange: onChangeFromOrigin, onSearch: onSearchOrigin, defaultValue,
    customOption: CustomOption, isOptionsLoading, maxTagCount, key, allowClear, isBadValueCheck, badValues, onlyAlphaNumeric,
    maxLengthValue, fieldLengthValue, onlyAlpha, onPanelLeave,
    suffixIcon, onSelect, hasDisabledPayers, HISPayerValue,
  } = formConfig;
  const { options = [] } = formConfig;
  let updatedFormConfig = formConfig;
  // only pass fieldInFocus when suggestedValuesMap is not undefined
  if (HISPayerValue) {
    updatedFormConfig = { ...formConfig, fieldInFocus };
  }

  const badValueRule = useMemo((): any => (isBadValueCheck ? [
    badValueValidation(key, badValues),
  ] : []), [isBadValueCheck]);

  useEffect((): any => {
    if (!onPanelLeave) {
      setIsFocused(onPanelLeave);
    }
  }, [onPanelLeave]);

  const onSearch = (event: any): void => {
    const validStr = processSearchInput(event, {
      onlyAlphaNumeric,
      onlyAlpha,
      maxLengthValue,
      fieldLengthValue,
      MAX_CHARACTERS_LIMIT,
    }, onSearchOrigin);

    setSearchValue(validStr);
    if (onSearchOrigin) {
      onSearchOrigin(validStr);
    }
  };

  const onMouseLeave = (event: any): any => {
    if (!event?.currentTarget?.contains(event?.relatedTarget)) {
      setIsFocused(false);
    }
  };

  const onChange = (event: any): void => {
    setSearchValue('');
    setExtraRules([]);
    if (onChangeFromOrigin) {
      onChangeFromOrigin(event);
    }
  };

  const handleDropdownVisibleChange = (e: any): void => {
    e?.selectRef?.current?.scrollTo?.(0);
    setIsFocused(false);
  };

  const onMouseEnter = (e: any): void => {
    setIsFocused(true);
  };

  const onClickDropdown = (e: any): any => {
    e?.current?.selectRef?.current?.scrollTo?.(0);
  };

  // TODO Add max width in tags
  function tagRender(tagProps: any): ReactElement {
    const { label: tagLabel, closable, onClose } = tagProps;
    return (
      <Tag closable={closable} onClose={onClose} >
        <span className="tag-name-length">
          {tagLabel}
        </span>
      </Tag>
    );
  }

  const inputOnKeyDown = (event: any): any => {
    setIsFocused(true);
    if (event.key === KEYS_TO_HANDLE?.Enter) {
      setIsFocused(false);
    }
  };

  const handleFocus = (): void => { // to notify user that there is no payer active for the provider
    isEmpty(options) && centeredNotification(NON_CONFIGURED_PAYERS_LIST_MESSAGE, 'info');
    setFieldInFocus(true);
  };
  const handleFilterOption = (input: string, option: any): boolean => {
    return ((option?.children || '')?.toString()?.toLowerCase()?.indexOf(input?.toLowerCase()) >= 0);
  };

  return <FormItem formConfig={updatedFormConfig} extraRules={[...extraRules, ...badValueRule]}>
    <Select
      allowClear={allowClear}
      data-testid={key}
      ref={selectRef}
      className={className}
      defaultValue={defaultValue}
      disabled={disabled}
      notFoundContent={isOptionsLoading ? <Spin size="small" /> : null}
      showSearch={typeof onSearchOrigin === 'boolean' ? onSearchOrigin : showSearch}
      placeholder={placeholder}
      onClick={onClickDropdown(selectRef)}
      tagRender={tagRender}
      open={isFocused}
      onKeyDown={(value: any): any => inputOnKeyDown(value)}
      mode={mode}
      maxTagCount={maxTagCount}
      onChange={(option: any): void => onChange(option)}
      onSearch={(event: any): any => onSearch(event)}
      onSelect={onSelect}
      onFocus = {handleFocus}
      optionFilterProp="children"
      searchValue={searchValue}
      suffixIcon={suffixIcon}
      dropdownClassName="custom-select-payer-dropdown"
      onBlur={(): any => {
        setSearchValue('');
        setIsFocused(false);
        setFieldInFocus(false);
      }}
      onMouseDown={onMouseEnter}
      dropdownRender={(menu: any): any => (
        <div onMouseLeave={onMouseLeave}>
          {menu}
        </div>
      )}
      filterOption={CustomOption ? false : handleFilterOption
      }
      onDropdownVisibleChange={(e: any): any => handleDropdownVisibleChange(selectRef?.current)}
    >
    { !isEmpty(options) &&
      <OptGroup label={PAYER_DROPDOWN_CONSTANTS?.ENABLED_PAYERS}>
        {
          options?.map((option: any): ReactElement | null => {
            let name = option;
            let value = option;
            if (option?.value && option?.name) {
              name = option?.name;
              value = option?.value || option?.name;
            }
            // Render options normally if showReconfigure is false or undefined and when the payer is not disabled
            if (!option?.showReconfigure && !option?.disabled) {
              return (
                <Option className="select-options" key={value} value={value}
                  title={name} data-testid={`${key}-${value}`}>
                  {CustomOption ? <CustomOption {...option} /> : name}
                </Option>
              );
            }
            return null;
          })
        }
        {
          options
            ?.filter((option: any): boolean => (option?.isOnMailPayer || option?.isRpaPayer) && option?.showReconfigure)
            ?.map((option: any): ReactElement | null => {
              // Render custom components if showReconfigure is true
              return option?.customComponent ? (
                <React.Fragment key={option?.value}>
                  {option.customComponent}
                </React.Fragment>
              ) : null;
            })
        }
      </OptGroup>
    }
      {hasDisabledPayers &&
        <OptGroup label={PAYER_DROPDOWN_CONSTANTS?.ENABLE_PENDING_PAYERS}>
          {
            options?.map((option: any): ReactElement => {
              return (
                option?.disabled &&
                option?.customComponent
              );
            })
          }
        </OptGroup>
      }
    </Select>
  </FormItem>;
};

export default FormSelectPayerDropdown;
